import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAnnouncements, createAnnouncement, deleteAnnouncement, getRoles, reactToAnnouncement, uploadImage } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, Megaphone, Trash2, AlertCircle, SmilePlus, ImagePlus } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import EmojiPicker from 'emoji-picker-react';

const REACTION_EMOJIS = ['👍', '❤️', '😂', '🎉', '🔥'];

export default function Announcements() {
  const { hasPermission, user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const emojiPickerRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
    priority: 'normal',
    target_roles: [],
    image_url: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      const [annRes, rolesRes] = await Promise.all([getAnnouncements(), getRoles()]);
      setAnnouncements(annRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      toast.error('Error al cargar anuncios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.content) {
      toast.error('Completa todos los campos requeridos');
      return;
    }
    try {
      await createAnnouncement(newAnnouncement);
      toast.success('Anuncio publicado');
      setShowCreate(false);
      setNewAnnouncement({ title: '', content: '', priority: 'normal', target_roles: [], visible_homepage: false, image_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear anuncio');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('¿Eliminar este anuncio?')) return;
    try {
      await deleteAnnouncement(id);
      toast.success('Anuncio eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar anuncio');
    }
  };

  const handleReaction = async (announcementId, emoji) => {
    try {
      const res = await reactToAnnouncement(announcementId, emoji);
      setAnnouncements(prev => prev.map(a => {
        if (a.id === announcementId) {
          return { ...a, reactions_summary: res.data.reactions, user_reactions: res.data.user_reactions };
        }
        return a;
      }));
    } catch {
      toast.error('Error al reaccionar');
    }
  };

  const onEmojiClick = (emojiData) => {
    setNewAnnouncement(prev => ({ ...prev, content: prev.content + emojiData.emoji }));
    setShowEmojiPicker(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const res = await uploadImage(file, 'announcement');
      setNewAnnouncement(prev => ({ ...prev, image_url: res.data.url }));
      toast.success('Imagen adjuntada');
    } catch {
      toast.error('Error al subir imagen');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d 'de' MMMM, yyyy", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      alta: 'bg-red-100 text-red-700 border-red-200',
      media: 'bg-amber-100 text-amber-700 border-amber-200',
      normal: 'bg-emerald-100 text-emerald-700 border-emerald-200'
    };
    const labels = { alta: 'Alta', media: 'Media', normal: 'Normal' };
    return { style: styles[priority] || styles.normal, label: labels[priority] || 'Normal' };
  };

  const canCreate = hasPermission('anuncios', 'crear');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="announcements-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-emerald-900 font-['Outfit']">
            Tablón de Anuncios
          </h1>
          <p className="text-muted-foreground">
            Comunicados importantes de la fraternidad
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setShowCreate(true)} data-testid="create-announcement-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Anuncio
          </Button>
        )}
      </div>

      <div className="grid gap-4">
        {announcements.length > 0 ? (
          announcements.map((announcement) => {
            const priority = getPriorityBadge(announcement.priority);
            const rSummary = announcement.reactions_summary || {};
            const userReacts = announcement.user_reactions || [];
            return (
              <Card key={announcement.id} className="card-hover" data-testid={`announcement-card-${announcement.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        announcement.priority === 'alta' ? 'bg-red-100' :
                        announcement.priority === 'media' ? 'bg-amber-100' : 'bg-emerald-100'
                      }`}>
                        {announcement.priority === 'alta' ? (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        ) : (
                          <Megaphone className={`w-5 h-5 ${
                            announcement.priority === 'media' ? 'text-amber-600' : 'text-emerald-600'
                          }`} />
                        )}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{announcement.title}</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {announcement.author_name && `Por ${announcement.author_name} · `}
                          {formatDate(announcement.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={priority.style}>{priority.label}</Badge>
                      {announcement.visible_homepage && <Badge className="bg-amber-100 text-amber-700 text-xs">En Homepage</Badge>}
                      {canCreate && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteAnnouncement(announcement.id)}
                          data-testid={`delete-announcement-${announcement.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-stone-600 whitespace-pre-wrap">{announcement.content}</p>
                  {announcement.image_url && (
                    <div className="mt-3 rounded-lg overflow-hidden border border-stone-100">
                      <img
                        src={announcement.image_url.startsWith('http') ? announcement.image_url : `${process.env.REACT_APP_BACKEND_URL}${announcement.image_url}`}
                        alt="Adjunto"
                        className="w-full max-h-80 object-cover"
                        data-testid={`announcement-image-${announcement.id}`}
                      />
                    </div>
                  )}
                  {/* Reactions */}
                  <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-stone-100" data-testid={`reactions-bar-${announcement.id}`}>
                    {REACTION_EMOJIS.map(emoji => {
                      const count = rSummary[emoji] || 0;
                      const isActive = userReacts.includes(emoji);
                      return (
                        <button
                          key={emoji}
                          onClick={() => handleReaction(announcement.id, emoji)}
                          data-testid={`reaction-${emoji}-${announcement.id}`}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm transition-all cursor-pointer border
                            ${isActive 
                              ? 'bg-emerald-100 border-emerald-400 text-emerald-800 shadow-sm' 
                              : 'bg-stone-50 border-stone-200 text-stone-500 hover:bg-stone-100 hover:border-stone-300'
                            }`}
                        >
                          <span className="text-base">{emoji}</span>
                          {count > 0 && <span className="font-medium text-xs">{count}</span>}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Megaphone className="w-12 h-12 mx-auto mb-4 text-stone-300" />
              <p className="text-muted-foreground">No hay anuncios publicados</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Announcement Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuevo Anuncio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                value={newAnnouncement.title}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                placeholder="Título del anuncio"
                data-testid="announcement-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Contenido *</Label>
              <div className="relative">
                <Textarea
                  ref={textareaRef}
                  value={newAnnouncement.content}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                  placeholder="Escribe el contenido del anuncio..."
                  rows={5}
                  data-testid="announcement-content"
                />
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="absolute bottom-2 right-2 p-1.5 rounded-md hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors"
                  data-testid="emoji-picker-toggle"
                >
                  <SmilePlus className="w-5 h-5" />
                </button>
                {showEmojiPicker && (
                  <div ref={emojiPickerRef} className="absolute bottom-12 right-0 z-50" data-testid="emoji-picker">
                    <EmojiPicker
                      onEmojiClick={onEmojiClick}
                      width={320}
                      height={380}
                      searchPlaceHolder="Buscar emoji..."
                      previewConfig={{ showPreview: false }}
                    />
                  </div>
                )}
              </div>
              {/* Image upload */}
              <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageUpload} className="hidden" data-testid="announcement-image-input" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
                className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-emerald-700 transition-colors mt-1"
                data-testid="announcement-image-upload-btn"
              >
                <ImagePlus className="w-4 h-4" />
                {uploadingImage ? 'Subiendo...' : newAnnouncement.image_url ? 'Cambiar imagen' : 'Adjuntar imagen'}
              </button>
              {newAnnouncement.image_url && (
                <div className="mt-2 relative inline-block">
                  <img src={newAnnouncement.image_url.startsWith('http') ? newAnnouncement.image_url : `${process.env.REACT_APP_BACKEND_URL}${newAnnouncement.image_url}`} alt="Preview" className="h-20 rounded border" />
                  <button type="button" onClick={() => setNewAnnouncement(p => ({ ...p, image_url: '' }))} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">&times;</button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Prioridad</Label>
              <Select
                value={newAnnouncement.priority}
                onValueChange={(value) => setNewAnnouncement({ ...newAnnouncement, priority: value })}
              >
                <SelectTrigger data-testid="announcement-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="media">Media</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 p-3 border rounded-lg bg-amber-50 border-amber-200">
              <input
                type="checkbox"
                id="visible_homepage"
                checked={newAnnouncement.visible_homepage || false}
                onChange={e => setNewAnnouncement({ ...newAnnouncement, visible_homepage: e.target.checked })}
                className="w-4 h-4 accent-amber-600"
                data-testid="announcement-visible-homepage"
              />
              <label htmlFor="visible_homepage" className="text-sm cursor-pointer">
                <span className="font-medium text-amber-800">Mostrar en Homepage pública</span>
                <span className="block text-xs text-amber-600">Este anuncio será visible para visitantes sin cuenta</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateAnnouncement} data-testid="submit-announcement-btn">
              Publicar Anuncio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
